/**
 * The MusicMentor mark.
 *
 * It was the Unicode character U+1D11E, which depends on a system font carrying
 * the Musical Symbols block. The Linux screenshots rendered it as a missing-glyph
 * box; macOS and iPadOS happen to have it, but a brand mark that can silently
 * become a tofu square is not a brand mark.
 *
 * Now a real G-clef outline, generated from the Bravura glyph data VexFlow
 * already ships and already uses to draw every staff in the app — see
 * `scripts/extract-clef.mjs`. That is deliberate: a hand-drawn approximation
 * looked like a wire loop rather than notation, and this is a clef whose spiral
 * genuinely sits where a clef's spiral sits. No new font, no new licence.
 *
 * Decorative by default. The adjacent "MusicMentor" text is the accessible brand
 * name, so the SVG is `aria-hidden` unless a `title` is passed for somewhere it
 * stands on its own.
 */

import { CLEF_ASPECT, CLEF_PATH, CLEF_VIEWBOX } from './clefPath';

export interface BrandMarkProps {
  /** Height in px. Width follows from the glyph's own proportions. */
  size?: number;
  /** Only for a mark with no adjacent wordmark. Leave unset when text follows. */
  title?: string;
  className?: string;
}

export function BrandMark({ size = 28, title, className }: BrandMarkProps) {
  return (
    <svg
      className={className}
      height={size}
      width={Math.round(size * CLEF_ASPECT * 100) / 100}
      viewBox={CLEF_VIEWBOX}
      // Filled, like real engraving: a clef has modulated weight, and a uniform
      // stroke is what made the first attempt read as a paperclip.
      fill="currentColor"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      <path d={CLEF_PATH} />
    </svg>
  );
}
